import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, Text, View } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import QRCode from 'qrcode';
import { useAuth } from './AuthContext';
import LoginScreen from './LoginScreen';
import { supabase } from '../lib/supabase';
import { traduzErro } from '../lib/erro';
import { C } from '../components/theme';
import { N_, t } from '../lib/i18n';

const COR = '#8B5CF6';
export const PREFIXO_QR = 'MPWEB1';

const PASSOS = [
  N_('Abra o My Planner no celular.'),
  N_('Vá em Ajustes › My Planner Web e toque em "Conectar navegador".'),
  N_('Aponte a câmera do celular para este QR Code.'),
];

/** Desenha o QR Code em SVG (funciona no navegador e no celular). */
export function QrImagem({ texto, tamanho = 260 }: { texto: string; tamanho?: number }) {
  const { d, n } = useMemo(() => {
    const q = QRCode.create(texto, { errorCorrectionLevel: 'M' });
    const n = q.modules.size; let d = '';
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (q.modules.get(y, x)) d += `M${x} ${y}h1v1h-1z`;
    return { d, n };
  }, [texto]);
  const m = 3; // margem em módulos
  return (
    <Svg width={tamanho} height={tamanho} viewBox={`${-m} ${-m} ${n + 2 * m} ${n + 2 * m}`}>
      <Rect x={-m} y={-m} width={n + 2 * m} height={n + 2 * m} fill="#fff" />
      <Path d={d} fill="#0E1015" />
    </Svg>
  );
}

type Codigo = { id: string; segredo: string; expira: number };

/** Tela de entrada do My Planner Web: mostra o QR Code e espera o celular aprovar. */
export default function WebQrLogin() {
  const a = useAuth();
  const [cpfSenha, setCpfSenha] = useState(false);
  const [cod, setCod] = useState<Codigo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [estado, setEstado] = useState<'gerando' | 'esperando' | 'expirado' | 'entrando'>('gerando');
  const [resta, setResta] = useState(0);
  const vivo = useRef(true);

  const gerar = useCallback(async () => {
    if (!supabase) return;
    setEstado('gerando'); setErro(null);
    const { data, error } = await supabase.rpc('qr_criar');
    if (!vivo.current) return;
    if (error || !data) {
      setErro(error?.message?.includes('qr_criar') ? t('O servidor ainda não foi preparado para o acesso pela web (rode supabase/migracao-0.6.sql).') : traduzErro(error));
      setEstado('expirado'); return;
    }
    setCod({ id: data.id, segredo: data.segredo, expira: Date.now() + 170 * 1000 });
    setEstado('esperando');
  }, []);

  useEffect(() => { vivo.current = true; gerar(); return () => { vivo.current = false; }; }, [gerar]);

  // Consulta a cada 2 segundos se o celular já aprovou.
  useEffect(() => {
    if (estado !== 'esperando' || !cod || !supabase) return;
    const timer = setInterval(async () => {
      const falta = Math.max(0, Math.round((cod.expira - Date.now()) / 1000));
      setResta(falta);
      if (!falta) { setEstado('expirado'); return; }
      const { data } = await supabase!.rpc('qr_status', { p_id: cod.id, p_segredo: cod.segredo });
      if (data?.status === 'aprovado' && data.token_hash) {
        setEstado('entrando');
        try { await a.entrarComToken?.(data.token_hash); }
        catch (e) { setErro(traduzErro(e)); setEstado('expirado'); }
      } else if (data?.status === 'expirado' || data?.status === 'invalido') setEstado('expirado');
    }, 2000);
    return () => clearInterval(timer);
  }, [estado, cod, a]);

  if (cpfSenha) {
    return (
      <View style={{ flex: 1, backgroundColor: C.bg, width: '100%', maxWidth: 560, alignSelf: 'center' }}>
        <LoginScreen />
        <Pressable onPress={() => setCpfSenha(false)} style={{ padding: 16, alignItems: 'center' }}>
          <Text style={{ color: COR, fontWeight: '700' }}>‹ {t('Voltar para o QR Code')}</Text>
        </Pressable>
      </View>
    );
  }

  const conteudo = cod ? `${PREFIXO_QR}:${cod.id}:${cod.segredo}` : '';
  return (
    <ScrollView style={{ flex: 1, backgroundColor: C.bg }} contentContainerStyle={{ padding: 24, alignItems: 'center', gap: 22, minHeight: '100%', justifyContent: 'center' }}>
      <View style={{ alignItems: 'center', gap: 10 }}>
        <Image source={require('../assets/icon.png')} style={{ width: 72, height: 72, borderRadius: 18 }} />
        <Text style={{ color: C.text, fontSize: 26, fontWeight: '800' }}>My Planner Web</Text>
        <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Use o seu planner no computador, sincronizado com o celular.')}</Text>
      </View>

      <View style={{ backgroundColor: C.card, borderRadius: 24, padding: 24, gap: 20, width: '100%', maxWidth: 720, flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ gap: 14, flex: 1, minWidth: 260 }}>
          {PASSOS.map((passo, i) => (
            <View key={i} style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
              <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: COR, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: '#fff', fontWeight: '800' }}>{i + 1}</Text>
              </View>
              <Text style={{ color: C.text, fontSize: 16, flex: 1, marginTop: 3 }}>{t(passo)}</Text>
            </View>
          ))}
          <Text style={{ color: C.mute, fontSize: 12 }}>{t('O QR Code muda a cada 3 minutos e só vale uma vez. Em computador compartilhado, use Ajustes › Sair deste computador ao terminar.')}</Text>
        </View>

        <View style={{ alignItems: 'center', gap: 10, width: 280 }}>
          <View style={{ width: 272, height: 272, borderRadius: 16, overflow: 'hidden', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
            {estado === 'esperando' && cod ? <QrImagem texto={conteudo} tamanho={272} />
              : estado === 'expirado' ? (
                <Pressable onPress={gerar} style={{ alignItems: 'center', gap: 10, padding: 20 }}>
                  <Text style={{ fontSize: 40 }}>🔄</Text>
                  <Text style={{ color: '#0E1015', fontWeight: '800', textAlign: 'center' }}>{t('Toque para gerar um novo QR Code')}</Text>
                </Pressable>
              ) : (
                <View style={{ alignItems: 'center', gap: 10 }}>
                  <ActivityIndicator color={COR} size="large" />
                  <Text style={{ color: '#0E1015' }}>{estado === 'entrando' ? t('Entrando...') : t('Gerando QR Code...')}</Text>
                </View>
              )}
          </View>
          {estado === 'esperando' && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Aguardando o celular · {s}s', { s: resta || 170 })}</Text>}
          {!!erro && <Text style={{ color: C.danger, fontSize: 13, textAlign: 'center' }}>{erro}</Text>}
        </View>
      </View>

      <Pressable onPress={() => setCpfSenha(true)}>
        <Text style={{ color: COR, fontWeight: '700' }}>{t('Entrar com CPF e senha')}</Text>
      </Pressable>
    </ScrollView>
  );
}
